import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  TextField,
  Divider,
  Alert,
  Stack
} from '@mui/material';
import {
  PlayCircleOutline,
  WhatsApp,
  PictureAsPdf,
  ArrowBack,
  PlayArrow,
  PictureAsPdfOutlined,
  LockOutlined
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import '../assets/styles/ArticleDetails.css';
import Header from '../components/common/Header';
import { fetchProductById } from '../store/productsSlice';
import { addToCart } from '../store/cartSlice';
import whatsappService from '../services/whatsapp.service';
import { formatPriceWithCurrency } from '../utils/formatPrice';
import ordersService from '../services/orders.service';
import productsService from '../services/products.service';
import ValEdIcon from '../components/common/ValEdIcon';

const ArticleDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const {
    items: products,
    currentProduct,
    loading,
  } = useSelector((state) => state.products);
  const { user } = useSelector((state) => state.auth);
  
  const [quantity, setQuantity] = useState(1);
  const [premiumStatus, setPremiumStatus] = useState('unknown');
  const [premiumReason, setPremiumReason] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!id) return;
    const numericId = Number(id);
    const cached = products.find((item) => item.id === numericId);
    if (!cached) {
      dispatch(fetchProductById(id));
    }
  }, [dispatch, id, products]);

  const product = useMemo(() => {
    const numericId = Number(id);
    const cached = products.find((item) => item.id === numericId);
    return cached || currentProduct;
  }, [products, currentProduct, id]);

  useEffect(() => {
    const checkPremiumAccess = async () => {
      if (!product || product.product_type !== 'virtual') {
        setPremiumStatus('allowed');
        return;
      }

      if (product.is_free) {
        setPremiumStatus('allowed');
        setPremiumReason('Contenu gratuit');
        return;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        setPremiumStatus('blocked');
        setPremiumReason('Connectez-vous pour accéder au contenu premium');
        return;
      }

      try {
        const accessCheck = await productsService.checkVirtualContentAccess(product.id, token);
        setPremiumStatus(accessCheck.canAccess ? 'allowed' : 'blocked');
        setPremiumReason(accessCheck.reason || 'Abonnement premium requis');
      } catch (error) {
        console.error('Erreur premium', error);
        setPremiumStatus('blocked');
        setPremiumReason('Impossible de vérifier l\'accès premium');
      }
    };

    checkPremiumAccess();
  }, [product]);

  const isPremiumVirtual = useMemo(() => product?.product_type === 'virtual' && !product.is_free, [product]);

  const handleReadContent = async () => {
    if (!product) return;
    
    // Pour les contenus payants, vérifier l'accès premium
    if (!product.is_free) {
      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Vous devez être connecté');
        return;
      }

      try {
        const accessCheck = await productsService.checkVirtualContentAccess(product.id, token);
        
        if (!accessCheck.canAccess) {
          toast.error(accessCheck.reason || 'Accès premium requis');
          return;
        }
      } catch (error) {
        console.error('Erreur vérification premium:', error);
        toast.error('Erreur lors de la vérification d\'accès');
        return;
      }
    }
    
    // Ouvrir le contenu selon le type dans la plateforme
    if (product.virtual_type === 'video' && product.virtual_url) {
      // Naviguer vers VideoDetails avec les données du produit
      navigate(`/video/${product.id}`, { 
        state: { 
          video: {
            id: product.id,
            title: product.title,
            description: product.description,
            url: product.virtual_url,
            thumbnail: product.image_url
          },
          product: product
        } 
      });
    } else if (product.virtual_type === 'pdf' && product.pdf_url) {
      // Naviguer vers PDFViewer avec les données du produit
      navigate(`/pdf/${product.id}`, { 
        state: { 
          product: product
        } 
      });
    } else {
      toast.error('Contenu non disponible');
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    // Vérifier le stock pour les produits physiques
    if (product.product_type === 'physical' && product.stock !== null) {
      if (product.stock === 0) {
        toast.error('  Produit en rupture de stock');
        return;
      }
      if (quantity > product.stock) {
        toast.error(`  Stock insuffisant ! Disponible: ${product.stock}, Demandé: ${quantity}`);
        return;
      }
    }
    
    dispatch(addToCart({ product, quantity }));
    toast.success(`${product.title} ajouté à la ValEd !`);
  };

  const handleWhatsAppOrder = async () => {
    if (!product) return;
    
    // Les produits virtuels ne peuvent pas être commandés via WhatsApp
    if (product.product_type === 'virtual') {
      toast.error('Les produits virtuels ne peuvent pas être commandés. Utilisez le bouton "Lire le contenu" pour y accéder.');
      return;
    }
    
    // Vérifier l'authentification
    const token = localStorage.getItem('access_token');
    if (!token) {
      toast.error('Vous devez être connecté pour passer une commande');
      navigate('/auth');
      return;
    }
    
    // Vérifier le stock pour les produits physiques
    if (product.stock !== null) {
      if (product.stock === 0) {
        toast.error('  Produit en rupture de stock');
        return;
      }
      if (quantity > product.stock) {
        toast.error(`  Stock insuffisant ! Disponible: ${product.stock}, Demandé: ${quantity}`);
        return;
      }
    }
    
    try {
      // Créer la commande dans la base de données
      const orderData = {
        product_id: product.id,
        quantity: quantity,
        delivery_address: deliveryAddress,
        phone: phone,
        notes: notes,
        order_source: 'whatsapp'
      };
      
      console.log('📦 Données de commande:', orderData);
      toast.info('⏳ Création de la commande...');
      
      const createdOrder = await ordersService.createOrder(orderData);
      console.log('   Commande créée:', createdOrder);
      
      // Ouvrir WhatsApp avec le message
      whatsappService.openWhatsAppOrder(product, quantity, user);
      
      // Détecter si on est sur mobile pour adapter le message
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        toast.success('   Commande créée ! Ouverture de WhatsApp...');
        // Sur mobile, pas de redirection automatique car l'utilisateur va vers WhatsApp
      } else {
        toast.success('Commande créée avec succès ! WhatsApp ouvert dans un nouvel onglet', { icon: false });
        // Sur desktop, rediriger vers mes commandes après un délai
        setTimeout(() => {
          navigate('/orders');
        }, 3000);
      }
      
    } catch (error) {
      console.error('  Erreur création commande:', error);
      console.error('  Détails erreur:', error.response?.data);
      
      if (error.status === 400) {
        toast.error(`  ${error.data?.error || 'Erreur de validation'}`);
      } else if (error.status === 401) {
        toast.error('  Session expirée, veuillez vous reconnecter');
        navigate('/auth');
      } else {
        toast.error(`  Erreur lors de la création de la commande: ${error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <Box  className="mobile-app-container has-fixed-header"   sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
        <Header />
        <Container sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Chargement du produit...</Typography>
        </Container>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box className="mobile-app-container has-fixed-header" sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
        <Header />
        <Container sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Produit introuvable
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/articles')}
            sx={{ mt: 2 }}
          >
            Retour à la boutique
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="mobile-app-container has-fixed-header article-details-container">
      <Header />
      
      <Container maxWidth="sm" sx={{ py: 4, px: 2, margin: '0 auto', width: '100%' }}>
        {/* Bouton retour */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/articles')}
          sx={{ mb: 3, color: '#784e3c' }}
        >
          Retour à la boutique
        </Button>

        <Stack spacing={4} className="article-details-grid">
          {/* Image et badges */}
          <Box sx={{ position: 'relative', width: '100%' }}>
            <img
              src={product.image_url || 'https://via.placeholder.com/500x500?text=Produit'}
              alt={product.title}
              style={{
                width: '100%',
                maxHeight: '500px',
                objectFit: 'cover',
                borderRadius: '20px',
                boxShadow: '0px 4px 20px rgba(0,0,0,0.1)'
              }}
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/500x500?text=Image+non+disponible';
              }}
            />

            {/* Badges */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                right: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Chip
                  label={product.product_type === 'physical' ? ' Physique' : ' Virtuel'}
                  sx={{
                    bgcolor: 'white',
                    fontWeight: 'bold'
                  }}
                />
                {product.product_type === 'virtual' && product.virtual_type && (
                  <Chip
                    icon={product.virtual_type === 'video' ? <PlayArrow /> : <PictureAsPdfOutlined />}
                    label={product.virtual_type === 'video' ? 'VIDÉO' : 'PDF'}
                    sx={{
                      bgcolor: product.virtual_type === 'video' ? '#FF5722' : '#2196F3',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem'
                    }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                {product.is_free && (
                  <Chip
                    label="GRATUIT"
                    sx={{
                      bgcolor: '#4CAF50',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                {!product.is_free && product.product_type === 'virtual' && (
                  <Chip
                    label="PREMIUM"
                    sx={{
                      bgcolor: '#FF9800',
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Détails et actions */}
          <Box className="article-details-content">
              {/* Zone titre avec hauteur FIXE */}
              <Box className="article-title-zone">
                <Typography
                  variant="h3"
                  className="article-title"
                >
                  {product.title}
                </Typography>
              </Box>

            {/* Prix */}
            {/* Prix seulement pour les produits physiques */}
            {product.product_type === 'physical' && (
              <Typography
                variant="h4"
                sx={{
                  color: product.is_free ? '#4CAF50' : '#784e3c',
                  fontWeight: 'bold',
                  fontFamily: 'Achiko',
                  mb: 3
                }}
              >
                {product.is_free ? 'Gratuit' : formatPriceWithCurrency(product.price)}
              </Typography>
            )}

              <Divider sx={{ my: 2 }} />

              {/* Zone description avec hauteur FIXE */}
              <Box className="article-description-zone">
                <Typography
                  variant="body1"
                  className="article-description"
                >
                  {product.description || 'Aucune description disponible'}
                </Typography>
              </Box>

              {/* Informations complémentaires avec hauteur FIXE */}
              <Card className="article-info-card">
                <CardContent sx={{ pb: '16px !important' }}>
                  <Grid container spacing={2}>
                  {product.product_type === 'physical' && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' ,color: '#784e3c' }}>
                        Stock disponible
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem',color: '#784e3c' }}>
                        {product.stock || 0} unités
                      </Typography>
                    </Grid>
                  )}
                  {product.product_type === 'virtual' && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Type de contenu
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {product.virtual_type === 'video' ? '🎥 Vidéo' :
                         product.virtual_type === 'pdf' ? '📄 PDF' : '📦 Autre'}
                      </Typography>
                    </Grid>
                  )}
                  {product.profilType && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem',color: '#784e3c' }}>
                        Profil recommandé
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem',color: '#784e3c' }}>
                        {product.profilType.name}
                      </Typography>
                    </Grid>
                  )}
                  {product.agesSection && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem',color: '#784e3c' }}>
                        Tranche d'âge
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '1rem',color: '#784e3c' }}>
                        {product.agesSection.name}
                      </Typography>
                    </Grid>
                  )}
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Zone boutons avec structure STANDARDISÉE */}
              <Box className="article-buttons-zone">
                {/* Structure identique pour TOUS les types */}
                <Box className="article-buttons-container" sx={{ 
                  width: '100%', 
                  maxWidth: '400px', 
                  margin: '0 auto', 
                  padding: '0 20px',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  {product.product_type === 'virtual' ? (
                    // Produits virtuels - Alert + Bouton
                    <>
                      <Alert 
                        severity={product.is_free ? 'success' : 'info'} 
                        sx={{ 
                          mb: 2, 
                          width: '100%', 
                          boxSizing: 'border-box',
                          margin: '0 0 16px 0',
                          marginLeft: '0 !important',
                          marginRight: '0 !important'
                        }}
                      >
                        {product.is_free ? 
                          'Ce contenu virtuel est gratuit ! Profitez-en dès maintenant.' :
                          (premiumStatus === 'allowed' ? 'Vous avez accès à ce contenu premium.' : premiumReason || 'Ce contenu est réservé aux membres Premium.')}
                      </Alert>
                      
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handleReadContent}
                        disabled={!product.is_free && premiumStatus !== 'allowed'}
                        startIcon={product.virtual_type === 'video' ? <PlayCircleOutline /> : <PictureAsPdf />}
                        sx={{
                          bgcolor: product.is_free ? '#20BA5A' :
                                  premiumStatus === 'allowed' ? '#FF9800' : '#E0E0E0',
                          py: 1.5,
                          fontFamily: 'Achiko',
                          fontWeight: 'bold',
                          color: '#FFFFFF',
                          textTransform: 'none',
                          borderRadius: '999px',
                          justifyContent: 'center',
                          mb: 0,
                          width: '100%',
                          margin: '0',
                          marginLeft: '0 !important',
                          marginRight: '0 !important',
                          boxSizing: 'border-box',
                          '&:hover': { 
                            bgcolor: product.is_free ? '#25D366' :
                                    premiumStatus === 'allowed' ? '#FB8C00' : '#E0E0E0'
                          },
                          '&.Mui-disabled': {
                            bgcolor: '#E0E0E0',
                            color: '#8D8D8D'
                          }
                        }}
                      >
                        {product.is_free ?
                          (product.virtual_type === 'video' ? 'Regarder la vidéo' : 'Lire le PDF') :
                         premiumStatus === 'allowed' ?
                          (product.virtual_type === 'video' ? 'Regarder la vidéo' : 'Lire le PDF') :
                          'Contenu Premium'}
                      </Button>
                    </>
                  ) : (
                    // Produits physiques - Quantité + Boutons
                    <>
                      <Alert 
                        severity="info" 
                        sx={{ 
                          mb: 2, 
                          width: '100%', 
                          boxSizing: 'border-box',
                          margin: '0 0 16px 0',
                          marginLeft: '0 !important',
                          marginRight: '0 !important'
                        }}
                      >
                        {`Produit disponible - ${product.stock || 0} unités en stock`}
                      </Alert>

                      {/* Quantité */}
                      <Box sx={{ mb: 2, width: '100%' }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Quantité
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                          <TextField
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                              const newValue = parseInt(e.target.value) || 1;
                              const maxStock = product.stock || 999;
                              const limitedValue = Math.min(Math.max(1, newValue), maxStock);
                              setQuantity(limitedValue);
                              
                              if (newValue > maxStock && product.stock !== null) {
                                toast.warning(`Quantité limitée à ${maxStock} (stock disponible)`);
                              }
                            }}
                            inputProps={{ min: 1, max: product.stock || 999 }}
                            size="small"
                            sx={{ width: 100 }}
                          />
                          {product.stock !== null && (
                            <Typography variant="caption" color={product.stock > 10 ? 'text.secondary' : product.stock > 0 ? 'warning.main' : 'error.main'}>
                              {product.stock > 0 ? `${product.stock} en stock` : 'Rupture de stock'}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Bouton Ajouter au ValEd */}
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handleAddToCart}
                        disabled={product.stock === 0 || quantity > product.stock}
                        startIcon={<ValEdIcon size={28} color="#8B4513" />}
                        sx={{
                          bgcolor: product.stock === 0 || quantity > product.stock ? '#ccc' : '#784e3c',
                          py: 1.5,
                          fontFamily: 'Achiko',
                          fontWeight: 'bold',
                          color: '#FFFFFF',
                          textTransform: 'none',
                          borderRadius: '999px',
                          justifyContent: 'center',
                          mb: 2,
                          width: '100%',
                          margin: '0 0 16px 0',
                          marginLeft: '0 !important',
                          marginRight: '0 !important',
                          boxSizing: 'border-box',
                          '&:hover': { bgcolor: product.stock === 0 || quantity > product.stock ? '#ccc' : '#3a9bc7' },
                          '&.Mui-disabled': {
                            bgcolor: '#ccc',
                            color: '#777'
                          }
                        }}
                      >
                        {product.stock === 0 ? 'Rupture de stock' : 
                         quantity > product.stock ? 'Quantité trop élevée' : 'Ajouter à la  ValEd'}
                      </Button>

                      {/* Séparateur */}
                      <Divider sx={{ my: 2, width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          ou
                        </Typography>
                      </Divider>

                      {/* Bouton WhatsApp */}
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handleWhatsAppOrder}
                        disabled={product.stock === 0 || quantity > product.stock}
                        startIcon={<WhatsApp />}
                        sx={{
                          bgcolor: product.stock === 0 || quantity > product.stock ? '#ccc' : '#25D366',
                          py: 1.5,
                          fontFamily: 'Achiko',
                          fontWeight: 'bold',
                          color: '#FFFFFF',
                          textTransform: 'none',
                          borderRadius: '999px',
                          justifyContent: 'center',
                          mb: 0,
                          width: '100%',
                          margin: '0',
                          marginLeft: '0 !important',
                          marginRight: '0 !important',
                          boxSizing: 'border-box',
                          '&:hover': { bgcolor: product.stock === 0 || quantity > product.stock ? '#ccc' : '#20BA5A' },
                          '&.Mui-disabled': {
                            bgcolor: '#ccc',
                            color: '#777'
                          }
                        }}
                      >
                        {product.stock === 0 ? 'Rupture de stock' : 
                         quantity > product.stock ? 'Quantité trop élevée' : 'Commander via WhatsApp'}
                      </Button>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Commande directe avec notre équipe
                      </Typography>
                    </>
                  )}
                </Box>
            </Box>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default ArticleDetails;